<template>
  <f7-app :params="f7params">
    <f7-panel left cover theme-dark :visible-breakpoint="992">
      <f7-view name="leftPanel" url="/main-panel"></f7-view>
    </f7-panel>

    <f7-view main class="safe-areas" url="/"></f7-view>

    <!-- Login screen -->
    <f7-login-screen
      id="loginScreen"
      class="login-screen"
      :opened="!userIsAauthorized"
    >
      <f7-view>
        <f7-page login-screen>
          <img
            slot="fixed"
            src="static/flower1.svg"
            alt="alt"
            class="popup-auth-decor first"
          />
          <img
            slot="fixed"
            src="static/flower2.svg"
            alt="alt"
            class="popup-auth-decor second"
          />
          <img
            slot="fixed"
            src="static/flower3.svg"
            alt="alt"
            class="popup-auth-decor third"
          />

          <f7-login-screen-title>
            <h2>{{ loginScreenTitle.title }}</h2>
            <span>{{ loginScreenTitle.subtitle }}</span>
          </f7-login-screen-title>

          <div v-if="loginScreen.step === 1">
            <f7-list>
              <f7-list-input
                type="text"
                floating-label
                clear-button
                :label="$t(`form_fields.phone_number.placeholder`)"
                :error-message-force="!!loginScreen.phone.error"
                :error-message="loginScreen.phone.error"
                :value="loginScreen.phone.value"
                @input="loginScreen.phone.value = $event.target.value"
              ></f7-list-input>
            </f7-list>
          </div>

          <div v-if="loginScreen.step === 2">
            <code-input
              :loading="false"
              :fields="4"
              :auto-focus="true"
              @change="(val) => (loginScreen.smsCode = val)"
              @complete="signIn"
            />
          </div>

          <f7-list v-if="loginScreen.step === 3" no-hairlines>
            <f7-list-input
              type="text"
              floating-label
              clear-button
              :label="$t(`form_fields.name.placeholder`)"
              :error-message-force="!!loginScreen.name.error"
              :error-message="loginScreen.name.error"
              :value="loginScreen.name.value"
              @input="loginScreen.name.value = $event.target.value"
            ></f7-list-input>

            <f7-list-input
              type="text"
              floating-label
              clear-button
              :label="$t(`form_fields.last_name.placeholder`)"
              :error-message-force="!!loginScreen.lastName.error"
              :error-message="loginScreen.lastName.error"
              :value="loginScreen.lastName.value"
              @input="loginScreen.lastName.value = $event.target.value"
            ></f7-list-input>

            <f7-list-input
              type="email"
              inputmode="email"
              floating-label
              clear-button
              :label="$t(`form_fields.email.placeholder`)"
              :error-message-force="!!loginScreen.email.error"
              :error-message="loginScreen.email.error"
              :value="loginScreen.email.value"
              @input="loginScreen.email.value = $event.target.value"
            ></f7-list-input>

            <f7-list-input
              type="text"
              floating-label
              clear-button
              :label="$t(`form_fields.referral.placeholder`)"
              :error-message-force="!!loginScreen.referral.error"
              :error-message="loginScreen.referral.error"
              :value="loginScreen.referral.value"
              @input="loginScreen.referral.value = $event.target.value"
            ></f7-list-input>
          </f7-list>

          <f7-block>
            <f7-button
              class="custome-large gradient"
              fill
              large
              @click="signIn"
            >
              {{ loginScreenButtonLabel }}
              <f7-preloader
                v-if="loginScreen.loading"
                color="white"
              ></f7-preloader>

              <f7-icon
                v-else
                f7="arrow_right"
                color="white"
                size="24"
              ></f7-icon>
            </f7-button>
          </f7-block>
        </f7-page>
      </f7-view>
    </f7-login-screen>
  </f7-app>
</template>

<script>
import { merge, isEmpty } from 'lodash';
import { mapActions, mapMutations, mapState } from 'vuex';

import routes from '@/js/routes.js';
import {
  getPreferredLanguage,
  getFullLangugeName,
  getUserLanguageFromLocalStorage,
  setUserLanguageToLocalStorage,
  setLangsToLocalStorage,
  setI18nLocale,
  setLangsToI18n
} from '@/js/helpers/languageUtils.js';
import notify from '@/js/helpers/notify.js';
import apiRequest from '@/js/helpers/apiRequest.js';
import wsActionManager from '@/js/helpers/wsActionManager.js';
import validateEmail from '@/js/helpers/validateEmail.js';
import setTokenToLocalStorage from '@/js/helpers/setTokenToLocalStorage.js';
import isTokenExpired from '@/js/helpers/isTokenExpired.js';

import CodeInput from 'vue-verification-code-input';

export default {
  components: {
    CodeInput
  },

  data() {
    return {
      f7params: {
        name: 'Real Lang Web Student',
        theme: 'ios',
        routes: routes,

        touch: {
          iosTouchRipple: true,
          touchRippleElements: '.ripple, .button'
        }
      },

      permissions: {
        mediaDevices: [],
        canEnumerate: false,
        hasMicrophone: false,
        hasSpeakers: false,
        hasWebcam: false
      },

      loginScreen: {
        step: 1,
        phone: { value: '', error: null },
        name: { value: '', error: null },
        lastName: { value: '', error: null },
        email: { value: '', error: null },
        referral: { value: '', error: null },
        smsCode: '',
        loginId: null,
        loading: false
      }
    };
  },

  computed: {
    loginScreenTitle() {
      const result = {};

      switch (this.loginScreen.step) {
        case 1:
          result.title = this.$t(`page_sign_in.phone_number_slide.title`);
          result.subtitle = this.$t(
            `page_sign_in.phone_number_slide.description`
          );
          break;
        case 2:
          result.title = this.$t(`page_sign_in.security_code_slide.title`);
          result.subtitle = this.$t(
            `page_sign_in.security_code_slide.description`
          );
          break;
        default:
          result.title = '';
          result.subtitle = '';
      }

      return result;
    },

    loginScreenButtonLabel() {
      return this.loginScreen.step === 1
        ? this.$t(`get_code`)
        : this.$t(`sign_in`);
    },

    userIsAauthorized() {
      return localStorage.getItem('access_token') && !isTokenExpired();
    },

    ...mapState({
      user: (state) => state.user.info,
      languages: (state) => state.languages,
      levels: (state) => state.levels,
      charging: (state) => state.charging,
      history: (state) => state.lesson.history,
      request: (state) => state.request.request,
      lesson: (state) => state.lesson.lesson
    })
  },

  created() {
    this.getAppConfig();

    if (localStorage.getItem('microphone')) {
      this.$store.commit('SET_USER_PERMISSIONS', {
        type: 'microphone',
        result: 'granted'
      });
    }

    if (localStorage.getItem('camera')) {
      this.$store.commit('SET_USER_PERMISSIONS', {
        type: 'camera',
        result: 'granted'
      });
    }
  },

  mounted() {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.enumerateDevices = function(callback) {
        navigator.mediaDevices.enumerateDevices().then(callback);
      };
    }

    if (
      typeof MediaStreamTrack !== 'undefined' &&
      'getSources' in MediaStreamTrack
    ) {
      this.permissions.canEnumerate = true;
    } else if (
      navigator.mediaDevices &&
      !!navigator.mediaDevices.enumerateDevices
    ) {
      this.permissions.canEnumerate = true;
    }

    // eslint-disable-next-line no-unused-vars
    this.$f7ready(async (f7) => {
      // console.log(f7.utils.colorThemeCSSProperties('#CE40B4'));
      getPreferredLanguage(f7, (lang) => {
        setUserLanguageToLocalStorage('nativeLang', lang);

        getUserLanguageFromLocalStorage(async (lang) => {
          try {
            const langs = await apiRequest('lang', 'GET', null);

            const locales = Object.getOwnPropertyNames(
              langs.success.messages
            ).map((lang) => ({
              shortName: lang,
              fullName: getFullLangugeName(lang)
            }));

            this.setLocales(locales);

            setLangsToLocalStorage(langs.success.messages);
            setI18nLocale(lang);
            setLangsToI18n(langs.success.messages);
          } catch (error) {
            setI18nLocale(lang);
            setLangsToI18n(JSON.parse(localStorage.getItem('langs')));
          }
        });
      });

      if (this.userIsAauthorized) {
        await this.getInitialData();
        this.getSecondaryData();
      }
    });
  },

  methods: {
    async getInitialData() {
      this.$f7.progressbar.show('pink');

      await Promise.all([
        this.getUserInfo(),
        this.getLanguages(),
        this.getLevels(),
        this.getAmounts(),
        this.getTeachersOnline(),
        this.getTerms()
      ]).then(() => {
        this.checkDeviceSupport(() => {
          if (this.permissions.hasMicrophone && this.permissions.hasWebcam) {
            navigator.permissions
              .query({ name: 'microphone' })
              .then((result) => {
                this.$store.commit('SET_USER_PERMISSIONS', {
                  type: 'microphone',
                  result: result.state
                });
              });
            navigator.permissions.query({ name: 'camera' }).then((result) => {
              this.$store.commit('SET_USER_PERMISSIONS', {
                type: 'camera',
                result: result.state
              });
            });
          }
        });

        this.$f7.progressbar.hide();
        this.initialDataReceived();
      });
    },

    async getSecondaryData() {
      this.secondaryDataReceived();
      await Promise.all([this.getLessonHistory(), this.checkUserStatus()]);
      this.secondaryDataReceived();

      this.$echo
        .private(`user.${this.user.id}`)
        .listen('UserNotification', (res) => {
          wsActionManager(res);
        });

      if (this.request && this.request.id) {
        this.$f7.views.leftPanel.router.navigate('/search-lesson', {
          clearPreviousHistory: true
        });
      }

      if (this.lesson && this.lesson.id) {
        this.$f7.views.main.router.navigate('/chat', {
          clearPreviousHistory: true
        });
      }
    },

    next() {
      this.loginScreen.step++;
    },

    validate() {
      const errors = {};
      const { name, lastName, email } = this.loginScreen;

      // Reset old errors
      name.error = null;
      lastName.error = null;
      email.error = null;

      if (!name.value) {
        errors.name = this.$t(`form_fields.errors.enter_name`);
      }

      if (!lastName.value) {
        errors.lastName = this.$t(`form_fields.errors.enter_last_name`);
      }

      if (!validateEmail(email.value)) {
        errors.email = this.$t(`form_fields.errors.enter_correct_email`);
      }

      if (!email.value) {
        errors.email = this.$t(`form_fields.errors.enter_email`);
      }

      if (isEmpty(errors)) {
        return true;
      }

      // Formating
      Object.keys(errors).forEach((error) => {
        errors[error] = {
          error: errors[error]
        };
      });

      merge(this.loginScreen.name, errors.name);
      merge(this.loginScreen.lastName, errors.lastName);
      merge(this.loginScreen.email, errors.email);
    },

    async signIn() {
      const { step } = this.loginScreen;

      if (step === 1) {
        const { phone } = this.loginScreen;

        if (!phone.value) {
          phone.error = this.$t(`form_fields.errors.field_required`);
          return;
        }

        phone.error = '';

        this.loginScreen.loading = true;

        try {
          const body = new FormData();
          body.append('phone', phone.value.replace(/\+|\-/g, ''));

          const res = await apiRequest('sendLoginSms', 'POST', body);

          this.loginScreen.loading = false;

          if (res.error) {
            notify({ text: res.error });
            return;
          }

          const { login_id: loginId } = res.success;

          this.loginScreen.loginId = loginId;
          localStorage.setItem('loginId', loginId);
          this.next();
        } catch (error) {
          this.loginScreen.loading = false;
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      } else if (step === 2) {
        const { loginId, smsCode } = this.loginScreen;

        if (!smsCode && smsCode.length !== 4) {
          notify({ text: this.$t(`form_fields.errors.field_required`) });
          return;
        }

        this.loginScreen.loading = true;

        try {
          const body = new FormData();

          body.append('login_id', loginId || localStorage.getItem('loginId'));
          body.append('code', smsCode);

          const res = await apiRequest('loginByCode', 'POST', body);

          if (res.error) {
            notify({ text: res.error });
            this.loginScreen.loading = false;
            return;
          }

          localStorage.removeItem('loginId');
          this.loginScreen.smsCode = '';
          setTokenToLocalStorage(res.success.token);
          this.$echo.connector.options.auth.headers[
            'Authorization'
          ] = `Bearer ${res.success.token}`;

          // Get user info
          await this.getUserInfo();

          if (!this.user.name || !this.user.lastName || !this.user.email) {
            this.user.name
              ? (this.loginScreen.name.value = this.user.name)
              : null;
            this.user.lastName
              ? (this.loginScreen.lastName.value = this.user.lastName)
              : null;
            this.user.email
              ? (this.loginScreen.email.value = this.user.email)
              : null;

            this.loginScreen.loading = false;
            this.next();
          } else {
            await this.getInitialData();

            this.loginScreen.loading = false;
            this.$f7.loginScreen.close();
            // notify({ text: 'Welcome back 🤟' });
            this.getSecondaryData();
          }
        } catch (error) {
          this.loginScreen.loading = false;
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      } else if (step === 3) {
        if (!this.validate()) {
          return;
        }

        const { name, lastName, email, referral } = this.loginScreen;

        this.loginScreen.loading = true;

        try {
          const body = new FormData();
          body.append('name', name.value);
          body.append('last_name', lastName.value);
          body.append('email', email.value);
          body.append('ref_code', referral.value);

          const res = await apiRequest('saveUserData', 'POST', body, true);

          if (res.error) {
            notify({ text: res.error });
            this.loginScreen.loading = false;
            return;
          }

          await this.getInitialData();

          this.loginScreen.loading = false;
          this.$f7.loginScreen.close();
          // notify({ text: 'Welcome back 🤟' });
          this.getSecondaryData();
        } catch (error) {
          this.loginScreen.loading = false;
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      }
    },

    async checkUserStatus() {
      try {
        const res = await apiRequest('request/check', 'GET', null, true);

        if (res.error) {
          notify({ text: res.error });
          return;
        }

        const { request: req, lesson: less } = res.success;

        if (req) {
          const { id, end, language_id, level_id, teachers_count } = req;

          const request = {
            id,
            end,
            languageId: language_id,
            levelId: level_id,
            teachersCount: teachers_count
          };

          this.setRequest({ request });
        }

        if (less) {
          const { id, start, video, end, messages: msg, teacher } = less;

          const lesson = {
            id,
            video,
            start,
            end,
            teacher
          };

          const messages = msg.map(
            ({
              id,
              mark,
              quote,
              quote_date,
              created_at,
              user_id,
              text,
              attachments
            }) => ({
              id,
              type: user_id === this.user.id ? 'sent' : 'received',
              mark,
              quote,
              quoteDate: quote_date,
              date: created_at,
              userId: user_id,
              text,
              attachment:
                attachments && attachments.length
                  ? {
                      src: attachments[0].src,
                      name: attachments[0].name,
                      type: attachments[0].type,
                      size: attachments[0].size
                    }
                  : null
            })
          );

          this.setLesson({ lesson });
          this.setMessages({ messages });
        }
      } catch (error) {
        console.error(error);
        notify({ text: this.$t(`notify.something_went_wrong`) });
      }
    },

    checkDeviceSupport(callback) {
      if (!this.permissions.canEnumerate) {
        return;
      }

      if (
        !navigator.enumerateDevices &&
        window.MediaStreamTrack &&
        window.MediaStreamTrack.getSources
      ) {
        navigator.enumerateDevices = window.MediaStreamTrack.getSources.bind(
          window.MediaStreamTrack
        );
      }

      if (!navigator.enumerateDevices && navigator.enumerateDevices) {
        navigator.enumerateDevices = navigator.enumerateDevices.bind(navigator);
      }

      if (!navigator.enumerateDevices) {
        if (callback) {
          callback();
        }
        return;
      }

      navigator.enumerateDevices((devices) => {
        devices.forEach((_device) => {
          const device = {};
          for (var d in _device) {
            device[d] = _device[d];
          }

          if (device.kind === 'audio') {
            device.kind = 'audioinput';
          }

          if (device.kind === 'video') {
            device.kind = 'videoinput';
          }

          let skip;
          this.permissions.mediaDevices.forEach((d) => {
            if (d.id === device.id && d.kind === device.kind) {
              skip = true;
            }
          });

          if (skip) {
            return;
          }

          if (!device.deviceId) {
            device.deviceId = device.id;
          }

          if (!device.id) {
            device.id = device.deviceId;
          }

          if (device.kind === 'audioinput') {
            this.permissions.hasMicrophone = true;
          }

          if (device.kind === 'audiooutput') {
            this.permissions.hasSpeakers = true;
          }

          if (device.kind === 'videoinput') {
            this.permissions.hasWebcam = true;
          }

          this.permissions.mediaDevices.push(device);
        });

        if (callback) {
          callback();
        }
      });
    },

    ...mapActions({
      getAppConfig: 'getAppConfig',
      getLanguages: 'getLanguages',
      getLevels: 'getLevels',
      getAmounts: 'getAmounts',
      getTeachersOnline: 'getTeachersOnline',
      getTerms: 'getTerms',
      getUserInfo: 'user/getUserInfo',
      getLessonHistory: 'lesson/getLessonHistory'
    }),

    ...mapMutations({
      setLocales: 'SET_LOCALES',
      initialDataReceived: 'INITIAL_DATA_RECEIVED',
      secondaryDataReceived: 'SECONDARY_DATA_RECEIVED',
      setRequest: 'request/SET_REQUEST',
      setLesson: 'lesson/SET_LESSON',
      setMessages: 'chat/SET_MESSAGES'
    })
  }
};
</script>

<style lang="less">
.popup-auth-decor {
  position: absolute;

  &.first {
    top: 80px;
    right: 50px;
  }

  &.second {
    bottom: 80px;
    right: 50px;
  }

  &.third {
    bottom: 20%;
    left: 50px;
  }
}
</style>
